import React, { useMemo } from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  formatCompactDate,
  getCategoryColors,
  getCategoryMeta,
  getTherapyPrepCategory,
  isValidDate,
  toEntryDate,
} from "./reportBuilders/dailyCareReportEntryUtils";

const toSentenceCase = (value = "") => {
  const text = String(value || "").toLowerCase();
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : "";
};

const ChartCard = ({ eyebrow, title, takeaway, children, theme }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 1.7, sm: 2 },
      borderRadius: "18px",
      bgcolor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.semantic.line}`,
      boxShadow: `0 12px 28px ${alpha(theme.palette.text.primary, 0.05)}`,
    }}
  >
    <Box sx={{ mb: 1.8 }}>
      <Typography
        sx={{
          color: theme.palette.semantic.sageDeep,
          fontSize: "0.68rem",
          fontWeight: 800,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          mb: 0.55,
        }}
      >
        {eyebrow}
      </Typography>
      <Typography sx={{ color: "text.primary", fontWeight: 850, fontSize: "1.1rem", lineHeight: 1.25 }}>
        {title}
      </Typography>
      {takeaway ? (
        <Typography sx={{ mt: 0.45, color: "text.secondary", fontSize: "0.92rem", lineHeight: 1.45 }}>
          {takeaway}
        </Typography>
      ) : null}
    </Box>
    {children}
  </Paper>
);

const buildCategoryRows = (entries = [], theme) => {
  const counts = entries.reduce((acc, entry) => {
    const category = getTherapyPrepCategory(entry) || "log";
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {});

  return Object.entries(counts)
    .map(([category, count]) => ({
      category,
      count,
      meta: getCategoryMeta({ type: category, category }),
      colors: getCategoryColors({ type: category, category }, theme),
      label: toSentenceCase(getCategoryMeta({ type: category, category })?.label || category),
    }))
    .sort((left, right) => right.count - left.count)
    .slice(0, 6);
};

const isFinitePositiveNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0;
};

const parseDurationHoursFromText = (value) => {
  const text = String(value || "").trim().toLowerCase();
  if (!text) {
    return null;
  }

  const hourMatch = text.match(/(\d+(?:\.\d+)?)\s*(?:h|hr|hrs|hour|hours)\b/);
  const minuteMatch = text.match(/(\d+(?:\.\d+)?)\s*(?:m|min|mins|minute|minutes)\b/);
  const hours = hourMatch ? Number(hourMatch[1]) : 0;
  const minutes = minuteMatch ? Number(minuteMatch[1]) : 0;

  if (!hourMatch && !minuteMatch) {
    return null;
  }

  const totalHours = hours + (minutes / 60);
  return totalHours > 0 ? Number(totalHours.toFixed(1)) : null;
};

const coerceEntryDate = (value) => {
  const date = toEntryDate(value);
  return isValidDate(date) ? date : null;
};

const getSleepDurationHours = (entry = {}) => {
  const sleepDetails = entry.sleepDetails || entry.data?.sleepDetails || entry.originalData?.sleepDetails || {};
  const durationHours = [
    sleepDetails.durationHours,
    sleepDetails.hours,
    entry.durationHours,
    entry.sleepDurationHours,
  ].find(isFinitePositiveNumber);

  if (durationHours != null) {
    return Number(Number(durationHours).toFixed(1));
  }

  const durationMinutes = [
    sleepDetails.durationMin,
    sleepDetails.durationMinutes,
    sleepDetails.duration,
    entry.durationMin,
    entry.durationMinutes,
    entry.sleepDuration,
  ].find(isFinitePositiveNumber);

  if (durationMinutes != null) {
    return Number((Number(durationMinutes) / 60).toFixed(1));
  }

  const durationTextHours = [
    sleepDetails.durationLabel,
    sleepDetails.durationText,
    entry.durationLabel,
    entry.duration,
    entry.sleepDurationLabel,
  ].map(parseDurationHoursFromText).find(isFinitePositiveNumber);

  if (durationTextHours != null) {
    return durationTextHours;
  }

  const startAt = coerceEntryDate(
    sleepDetails.startAt
    || sleepDetails.startTime
    || sleepDetails.bedtime
    || entry.startAt
    || entry.startTime
    || entry.bedtime
  );
  const endAt = coerceEntryDate(
    sleepDetails.endAt
    || sleepDetails.endTime
    || sleepDetails.wakeTime
    || entry.endAt
    || entry.endTime
    || entry.wakeTime
  );

  if (startAt && endAt) {
    const elapsedHours = (endAt.getTime() - startAt.getTime()) / (60 * 60 * 1000);
    return elapsedHours > 0 ? Number(elapsedHours.toFixed(1)) : null;
  }

  return null;
};

const buildSleepBehaviorRows = (entries = []) => {
  const dayMap = entries.reduce((acc, entry) => {
    const date = toEntryDate(entry.timestamp || entry.date || entry.createdAt);
    if (!isValidDate(date)) {
      return acc;
    }

    const key = new Date(date.getFullYear(), date.getMonth(), date.getDate()).toISOString();
    if (!acc[key]) {
      acc[key] = {
        key,
        date,
        sleepEntries: 0,
        sleepHours: 0,
        sleepEntriesWithoutDuration: 0,
        behaviorCount: 0,
      };
    }

    const category = getTherapyPrepCategory(entry);
    if (category === "sleep") {
      acc[key].sleepEntries += 1;
      const sleepHours = getSleepDurationHours(entry);
      if (isFinitePositiveNumber(sleepHours)) {
        acc[key].sleepHours += sleepHours;
      } else {
        acc[key].sleepEntriesWithoutDuration += 1;
      }
    }
    if (category === "behavior") {
      acc[key].behaviorCount += 1;
    }
    return acc;
  }, {});

  return Object.values(dayMap)
    .map((row) => ({
      ...row,
      sleepHours: row.sleepHours ? Number(row.sleepHours.toFixed(1)) : 0,
    }))
    .filter((row) => row.sleepEntries || row.behaviorCount)
    .sort((left, right) => left.date - right.date);
};

export const MostLoggedAreasChart = ({ entries = [], theme }) => {
  const rows = useMemo(() => buildCategoryRows(entries, theme), [entries, theme]);
  const maxCount = Math.max(1, ...rows.map((row) => row.count));
  const topArea = rows[0];
  const takeaway = topArea
    ? `${topArea.label} shows up most in this range.`
    : "No logged areas yet for this range.";

  return (
    <ChartCard
      eyebrow="Activity"
      title="Logged areas"
      takeaway={takeaway}
      theme={theme}
    >
      {rows.length ? (
        <Stack spacing={1.1}>
          {rows.map((row) => (
            <Box key={row.category}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.45 }}>
                <Typography sx={{ color: "text.primary", fontWeight: 800, fontSize: "0.95rem" }}>
                  {row.label}
                </Typography>
                <Typography sx={{ ml: "auto", color: "text.secondary", fontSize: "0.9rem", fontWeight: 800 }}>
                  {row.count}
                </Typography>
              </Box>
              <Box sx={{ height: 10, borderRadius: "999px", bgcolor: alpha(theme.palette.text.primary, 0.1), overflow: "hidden" }}>
                <Box
                  sx={{
                    width: `${Math.max(8, (row.count / maxCount) * 100)}%`,
                    height: "100%",
                    borderRadius: "999px",
                    bgcolor: row.colors.text,
                  }}
                />
              </Box>
            </Box>
          ))}
        </Stack>
      ) : (
        <Box
          sx={{
            p: 1.5,
            borderRadius: "14px",
            bgcolor: alpha(theme.palette.semantic.sageMist, 0.35),
            border: `1px dashed ${alpha(theme.palette.semantic.sageDeep, 0.24)}`,
          }}
        >
          <Typography sx={{ color: "text.secondary", lineHeight: 1.55 }}>
            Add a few logs in this range and Lifelog will show which care areas are being tracked most.
          </Typography>
        </Box>
      )}
    </ChartCard>
  );
};

export const SleepBehaviorTogetherChart = ({ entries = [], theme }) => {
  const rows = useMemo(() => buildSleepBehaviorRows(entries), [entries]);
  const sleepRows = rows.filter((row) => row.sleepEntries);
  const behaviorRows = rows.filter((row) => row.behaviorCount);
  const daysWithSleepHours = rows.filter((row) => row.sleepHours > 0).length;
  const daysWithBehavior = rows.filter((row) => row.behaviorCount).length;
  const maxBehaviorCount = Math.max(1, ...behaviorRows.map((row) => row.behaviorCount));
  const totalSleepEntries = sleepRows.reduce((total, row) => total + row.sleepEntries, 0);
  const missingDurationCount = rows.reduce((total, row) => total + row.sleepEntriesWithoutDuration, 0);
  const sleepTone = theme.palette.semantic.sleep || theme.palette.semantic.categories.sleep;
  const behaviorTone = theme.palette.semantic.behavior || theme.palette.semantic.categories.behavior;
  const sleepTakeaway = (() => {
    if (!sleepRows.length) {
      return "Sleep notes will appear here when they are logged.";
    }
    if (!daysWithSleepHours) {
      return `Sleep was noted ${totalSleepEntries === 1 ? "once" : `${totalSleepEntries} times`} — add bedtime and wake time to see hours.`;
    }
    return `${daysWithSleepHours} ${daysWithSleepHours === 1 ? "day has" : "days have"} sleep hours in this range.`;
  })();
  const behaviorTakeaway = behaviorRows.length
    ? `${daysWithBehavior} ${daysWithBehavior === 1 ? "day has" : "days have"} behavior notes in this range.`
    : "Behavior notes will appear here when harder days are logged.";

  return (
    <Stack spacing={2.4}>
      <ChartCard
        eyebrow="Sleep"
        title="Sleep notes"
        takeaway={sleepTakeaway}
        theme={theme}
      >
        {sleepRows.length ? (
          <Stack spacing={0.75}>
            <Box
              sx={{
                p: 1.2,
                borderRadius: "14px",
                bgcolor: alpha(sleepTone.bg, 0.5),
                border: `1px solid ${alpha(sleepTone.ink, 0.14)}`,
              }}
            >
              <Typography sx={{ color: sleepTone.ink, fontSize: "0.86rem", fontWeight: 850, lineHeight: 1.35 }}>
                {daysWithSleepHours ? "Sleep hours available" : "Sleep hours not ready yet"}
              </Typography>
              <Typography sx={{ mt: 0.25, color: "text.secondary", fontSize: "0.86rem", lineHeight: 1.5 }}>
                {daysWithSleepHours
                  ? `${daysWithSleepHours} ${daysWithSleepHours === 1 ? "sleep note includes" : "sleep notes include"} bedtime and wake time.`
                  : `${missingDurationCount || totalSleepEntries} ${totalSleepEntries === 1 ? "sleep note needs" : "sleep notes need"} bedtime and wake time before hours can be shown.`}
              </Typography>
            </Box>
          </Stack>
        ) : (
          <Box
            sx={{
              p: 1.5,
              borderRadius: "14px",
              bgcolor: alpha(theme.palette.semantic.sageMist, 0.35),
              border: `1px dashed ${alpha(theme.palette.semantic.sageDeep, 0.24)}`,
            }}
          >
            <Typography sx={{ color: "text.secondary", lineHeight: 1.55 }}>
              Add a sleep note to begin tracking rest.
            </Typography>
          </Box>
        )}
      </ChartCard>

      <ChartCard
        eyebrow="Behavior"
        title="Harder days"
        takeaway={behaviorTakeaway}
        theme={theme}
      >
        {behaviorRows.length ? (
          <Stack spacing={0.9}>
            {behaviorRows.slice(0, 10).map((row) => (
              <Box
                key={row.key}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "74px minmax(0, 1fr) 36px",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Typography sx={{ color: "text.secondary", fontSize: "0.84rem", fontWeight: 800 }}>
                  {formatCompactDate(row.date)}
                </Typography>
                <Box sx={{ height: 11, borderRadius: "999px", bgcolor: alpha(theme.palette.text.primary, 0.08), overflow: "hidden" }}>
                  <Box
                    sx={{
                      width: `${Math.max(10, (row.behaviorCount / maxBehaviorCount) * 100)}%`,
                      height: "100%",
                      borderRadius: "999px",
                      bgcolor: behaviorTone.ink,
                    }}
                  />
                </Box>
                <Typography sx={{ color: "text.secondary", fontSize: "0.8rem", fontWeight: 850, textAlign: "right" }}>
                  {row.behaviorCount}
                </Typography>
              </Box>
            ))}
          </Stack>
        ) : (
          <Box
            sx={{
              p: 1.5,
              borderRadius: "14px",
              bgcolor: alpha(theme.palette.semantic.sageMist, 0.35),
              border: `1px dashed ${alpha(theme.palette.semantic.sageDeep, 0.24)}`,
            }}
          >
            <Typography sx={{ color: "text.secondary", lineHeight: 1.55 }}>
              Behavior notes will appear here when harder days are logged.
            </Typography>
          </Box>
        )}
      </ChartCard>
    </Stack>
  );
};

export default MostLoggedAreasChart;
